import { useState, useRef, useCallback, useEffect } from 'react';
import {
  base64ToUint8Array,
  decodeAudioData,
  createPcmBlob,
  createPcmBytes,
  arrayBufferToBase64,
  calculateVolume
} from '@/utils/audioUtils';
import { parseConfidence, type ConfidenceLevel, type ConfidenceResult } from '@/lib/confidence';
import { useDocumentContextStore } from '@/stores/documentContextStore';

export type LiveConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

export type UserLevel = 'trainee' | 'junior' | 'senior';

export interface LiveMessage {
  id: string;
  role: 'user' | 'model' | 'system';
  text: string;
  timestamp: Date;
  // Streaming state for real-time display (BUG-001 fix)
  isStreaming?: boolean;
  // Confidence fields (only for model messages)
  confidence?: ConfidenceLevel;
  confidencePercentage?: number;
  confidenceReason?: string;
  requiresReview?: boolean;
  reviewedAt?: Date;
  // User feedback (BUG-002: thumbs up/down)
  feedback?: 'positive' | 'negative';
  // Generated image (BUG-016)
  imageBase64?: string;
  imageMimeType?: string;
  imageId?: string;
  // Generated document (EPIC-17)
  documentId?: string;
  documentTitle?: string;
  documentType?: string;
  documentFilename?: string;
  documentMimeType?: string;
}

interface UseGeminiLiveConfig {
  userLevel: UserLevel;
}

interface UseGeminiLiveReturn {
  // Connection state
  status: LiveConnectionStatus;
  error: string | null;

  // Audio state
  volume: number;
  isMuted: boolean;
  isVoiceEnabled: boolean;

  // Video state
  isVideoActive: boolean;
  facingMode: 'user' | 'environment';
  hasMultipleCameras: boolean;

  // Transcripts
  messages: LiveMessage[];

  // Review gate
  pendingReviews: number;
  acknowledgeMessage: (messageId: string) => void;
  submitFeedback: (messageId: string, feedback: 'positive' | 'negative') => void;
  canDisconnect: boolean;

  // Actions
  connect: (options?: { enableVoice?: boolean }) => Promise<void>;
  disconnect: () => void;
  toggleMute: () => void;
  enableVoice: () => Promise<void>;
  disableVoice: () => void;
  startVideo: () => Promise<void>;
  stopVideo: () => void;
  switchCamera: () => Promise<void>;
  sendImage: (file: File) => Promise<void>;
  capturePhoto: () => Blob | null; // BUG-032: Capture high-res photo from camera
  sendText: (text: string) => Promise<void>;
  sendDocumentContext: () => Promise<boolean>; // BUG-021: Send document context to active session

  // Refs for video elements
  previewVideoRef: React.RefObject<HTMLVideoElement | null>;

  // Idle timeout (BUG-011)
  idleWarning: boolean;
  resetIdleTimer: () => void;
}

// Get WebSocket URL for Live mode
// Note: Document context is sent via WebSocket message, not URL (to avoid URL length limits)
const getWsUrl = (level: string, token: string): string => {
  const wsUrl = import.meta.env.VITE_WS_URL;
  const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';

  let baseWsUrl: string;

  if (wsUrl) {
    baseWsUrl = wsUrl;
  } else if (apiUrl.startsWith('http')) {
    baseWsUrl = apiUrl.replace('http', 'ws');
  } else {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    baseWsUrl = `${protocol}//${window.location.host}${apiUrl}`;
  }

  return `${baseWsUrl}/ws/voice-live?token=${encodeURIComponent(token)}&level=${level}`;
};

/**
 * Hook for Gemini Live via Backend Proxy
 * 
 * This hook connects to the backend WebSocket proxy which then connects
 * to Gemini Live, keeping the API key secure on the server.
 * 
 * Features:
 * - Real-time bidirectional audio streaming
 * - Built-in voice activity detection (via Gemini)
 * - Video frame injection for multimodal
 * - ~100-300ms latency
 * - Auto-reconnection
 * - Secure (API key never exposed to frontend)
 */
export function useGeminiLive(config: UseGeminiLiveConfig): UseGeminiLiveReturn {
  const { userLevel } = config;
  
  // Connection state
  const [status, setStatus] = useState<LiveConnectionStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);
  
  // Audio state
  const [volume, setVolume] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(false);
  
  // Video state
  const [isVideoActive, setIsVideoActive] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  // Detect multiple cameras on mount
  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices().then(devices => {
      const videoInputs = devices.filter(d => d.kind === 'videoinput');
      setHasMultipleCameras(videoInputs.length > 1);
    }).catch(() => {
      setHasMultipleCameras(false);
    });
  }, []);

  // Transcripts
  const [messages, setMessages] = useState<LiveMessage[]>([]);

  // Calculate pending reviews (messages that require review but haven't been reviewed)
  const pendingReviews = messages.filter(
    m => m.requiresReview && !m.reviewedAt
  ).length;
  const canDisconnect = pendingReviews === 0;

  // Acknowledge a message (mark as reviewed)
  const acknowledgeMessage = useCallback((messageId: string) => {
    setMessages(prev =>
      prev.map(m =>
        m.id === messageId
          ? { ...m, reviewedAt: new Date() }
          : m
      )
    );
  }, []);

  // Submit feedback for a message (BUG-002 fix)
  const submitFeedback = useCallback((messageId: string, feedback: 'positive' | 'negative') => {
    setMessages(prev =>
      prev.map(m =>
        m.id === messageId
          ? { ...m, feedback, reviewedAt: new Date() }
          : m
      )
    );
    // Note: Backend persistence is handled by the component that calls this
  }, []);

  // Reset idle timer on activity (BUG-011 fix)
  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIdleWarning(false);

    // Clear existing timers
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    // Only set timers if connected
    if (status === 'connected') {
      // Warning timer at 4 minutes
      warningTimerRef.current = setTimeout(() => {
        setIdleWarning(true);
        console.log('Voice session idle warning: 1 minute until auto-disconnect');
      }, WARNING_TIMEOUT);

      // Disconnect timer at 5 minutes
      idleTimerRef.current = setTimeout(() => {
        console.log('Voice session auto-disconnecting due to inactivity');
        disconnect();
      }, IDLE_TIMEOUT);
    }
  }, [status]);

  // Clear idle timers (BUG-011 fix)
  const clearIdleTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    setIdleWarning(false);
  }, []);

  // WebSocket ref
  const wsRef = useRef<WebSocket | null>(null);
  
  // Audio contexts
  const inputAudioContextRef = useRef<AudioContext | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const inputNodeRef = useRef<GainNode | null>(null);
  const outputNodeRef = useRef<GainNode | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  
  // Video refs
  const videoStreamRef = useRef<MediaStream | null>(null);
  const processingVideoRef = useRef<HTMLVideoElement | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoIntervalRef = useRef<number | null>(null);
  
  // Playback timing
  const nextStartTimeRef = useRef<number>(0);
  const sourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());

  // Interrupt debounce: filters echo-triggered false interruptions
  const interruptDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Transcription refs
  const currentInputTranscriptionRef = useRef<string>('');
  const currentOutputTranscriptionRef = useRef<string>('');

  // Streaming message IDs for real-time display (BUG-001 fix)
  const streamingUserMessageIdRef = useRef<string | null>(null);
  const streamingModelMessageIdRef = useRef<string | null>(null);
  
  // Reconnection
  const isManualDisconnectRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptRef = useRef(0);
  const MAX_RETRIES = 5;

  // Track full connection status (after Gemini handshake)
  const isFullyConnectedRef = useRef(false);

  // Inactivity timeout (BUG-011 fix)
  const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
  const WARNING_TIMEOUT = 4 * 60 * 1000; // Warning at 4 minutes
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [idleWarning, setIdleWarning] = useState(false);
  const lastActivityRef = useRef<number>(Date.now());

  // Create hidden elements for video processing
  useEffect(() => {
    if (!processingVideoRef.current) {
      const video = document.createElement('video');
      video.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:1px;height:1px;';
      video.autoplay = true;
      video.playsInline = true;
      video.muted = true;
      document.body.appendChild(video);
      processingVideoRef.current = video;
    }
    
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    
    return () => {
      if (processingVideoRef.current && processingVideoRef.current.parentNode) {
        document.body.removeChild(processingVideoRef.current);
        processingVideoRef.current = null;
      }
    };
  }, []);

  // Play binary audio data (optimized path)
  const playBinaryAudio = useCallback(async (audioData: ArrayBuffer) => {
    if (!outputAudioContextRef.current) return;

    // Cancel pending interrupt debounce — new audio arrived, so the
    // interruption was a false trigger (echo/feedback)
    if (interruptDebounceRef.current) {
      clearTimeout(interruptDebounceRef.current);
      interruptDebounceRef.current = null;
    }

    const ctx = outputAudioContextRef.current;
    const now = ctx.currentTime;

    if (nextStartTimeRef.current < now) {
      nextStartTimeRef.current = now + 0.15; // 150ms lookahead to smooth proxy jitter
    }

    const audioBuffer = await decodeAudioData(
      new Uint8Array(audioData),
      ctx,
      24000,
      1
    );

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(outputNodeRef.current!);

    source.addEventListener('ended', () => {
      sourcesRef.current.delete(source);
      if (sourcesRef.current.size === 0) setVolume(0);
    });

    source.start(nextStartTimeRef.current);
    sourcesRef.current.add(source);
    nextStartTimeRef.current += audioBuffer.duration;
    setVolume(0.4);
  }, []);

  // Handle WebSocket messages from backend
  const handleWebSocketMessage = useCallback(async (event: MessageEvent) => {
    try {
      // Binary message = audio (optimized path)
      if (event.data instanceof Blob) {
        const audioData = await event.data.arrayBuffer();
        await playBinaryAudio(audioData);
        return;
      }

      if (event.data instanceof ArrayBuffer) {
        await playBinaryAudio(event.data);
        return;
      }

      // Text message = JSON control message
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'connected':
          setStatus('connected');
          setError(null);
          reconnectAttemptRef.current = 0;
          isFullyConnectedRef.current = true;
          break;

        case 'error':
          setError(data.message);
          if (data.message.includes('API key') || data.message.includes('auth')) {
            setStatus('error');
          }
          break;

        case 'interrupted':
          // Short debounce to filter echo-triggered false interruptions.
          // If new audio arrives within 100ms, the interruption was likely
          // echo — not the user speaking — and should be ignored.
          if (interruptDebounceRef.current) {
            clearTimeout(interruptDebounceRef.current);
          }
          interruptDebounceRef.current = setTimeout(() => {
            sourcesRef.current.forEach(source => {
              try { source.stop(); } catch (e) {}
            });
            sourcesRef.current.clear();
            nextStartTimeRef.current = 0;
            interruptDebounceRef.current = null;
          }, 100);
          break;

        case 'input_transcription':
          // BUG-001 fix: Show user transcription in real-time
          currentInputTranscriptionRef.current += data.text || '';
          const inputText = currentInputTranscriptionRef.current.trim();
          if (inputText) {
            // Create or update streaming user message
            if (!streamingUserMessageIdRef.current) {
              streamingUserMessageIdRef.current = `${Date.now()}-user-streaming`;
            }
            const userMsgId = streamingUserMessageIdRef.current;
            setMessages(prev => {
              const existingIdx = prev.findIndex(m => m.id === userMsgId);
              if (existingIdx >= 0) {
                // Update existing streaming message
                const updated = [...prev];
                updated[existingIdx] = {
                  ...updated[existingIdx],
                  text: inputText,
                };
                return updated;
              } else {
                // Add new streaming message
                return [...prev, {
                  id: userMsgId,
                  role: 'user',
                  text: inputText,
                  timestamp: new Date(),
                  isStreaming: true,
                }];
              }
            });
          }
          break;

        case 'output_transcription':
          // BUG-001 fix: Show model transcription in real-time
          currentOutputTranscriptionRef.current += data.text || '';
          const outputText = currentOutputTranscriptionRef.current.trim();
          if (outputText) {
            // Create or update streaming model message
            if (!streamingModelMessageIdRef.current) {
              streamingModelMessageIdRef.current = `${Date.now()}-model-streaming`;
            }
            const modelMsgId = streamingModelMessageIdRef.current;
            setMessages(prev => {
              const existingIdx = prev.findIndex(m => m.id === modelMsgId);
              if (existingIdx >= 0) {
                // Update existing streaming message
                const updated = [...prev];
                updated[existingIdx] = {
                  ...updated[existingIdx],
                  text: outputText,
                };
                return updated;
              } else {
                // Add new streaming message
                return [...prev, {
                  id: modelMsgId,
                  role: 'model',
                  text: outputText,
                  timestamp: new Date(),
                  isStreaming: true,
                }];
              }
            });
          }
          break;

        case 'turn_complete':
          // BUG-001 fix: Finalize streaming messages with confidence data
          const finalUserText = currentInputTranscriptionRef.current.trim();
          const finalModelText = currentOutputTranscriptionRef.current.trim();

          setMessages(prev => {
            let updated = [...prev];

            // Finalize user message (remove streaming flag)
            if (streamingUserMessageIdRef.current) {
              const userIdx = updated.findIndex(m => m.id === streamingUserMessageIdRef.current);
              if (userIdx >= 0 && finalUserText) {
                updated[userIdx] = {
                  ...updated[userIdx],
                  text: finalUserText,
                  isStreaming: false,
                };
              } else if (userIdx >= 0 && !finalUserText) {
                // Remove empty streaming message
                updated.splice(userIdx, 1);
              }
            }

            // Finalize model message with confidence data
            if (streamingModelMessageIdRef.current) {
              const modelIdx = updated.findIndex(m => m.id === streamingModelMessageIdRef.current);
              if (modelIdx >= 0 && finalModelText) {
                const confidenceResult = parseConfidence(finalModelText);
                updated[modelIdx] = {
                  ...updated[modelIdx],
                  text: finalModelText,
                  isStreaming: false,
                  confidence: confidenceResult.level,
                  confidencePercentage: confidenceResult.percentage,
                  confidenceReason: confidenceResult.reason,
                  requiresReview: confidenceResult.requiresReview,
                };
              } else if (modelIdx >= 0 && !finalModelText) {
                // Remove empty streaming message
                updated.splice(modelIdx, 1);
              }
            }

            return updated;
          });

          // Reset for next turn
          currentInputTranscriptionRef.current = '';
          currentOutputTranscriptionRef.current = '';
          streamingUserMessageIdRef.current = null;
          streamingModelMessageIdRef.current = null;
          break;

        case 'audio':
          // Legacy base64 audio (fallback for backwards compatibility)
          if (data.data && outputAudioContextRef.current) {
            const ctx = outputAudioContextRef.current;
            const now = ctx.currentTime;

            if (nextStartTimeRef.current < now) {
              nextStartTimeRef.current = now + 0.15; // 150ms lookahead to smooth proxy jitter
            }

            const audioBuffer = await decodeAudioData(
              base64ToUint8Array(data.data),
              ctx,
              24000,
              1
            );

            const source = ctx.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(outputNodeRef.current!);

            source.addEventListener('ended', () => {
              sourcesRef.current.delete(source);
              if (sourcesRef.current.size === 0) setVolume(0);
            });

            source.start(nextStartTimeRef.current);
            sourcesRef.current.add(source);
            nextStartTimeRef.current += audioBuffer.duration;
            setVolume(0.4);
          }
          break;

        case 'tool_call':
          // Log tool calls (search, code execution)
          console.log('Gemini tool call:', data.calls);
          break;

        case 'generation_progress':
          // BUG-026: Show generating indicator in chat
          setMessages(prev => [
            ...prev,
            {
              id: `${Date.now()}-generating`,
              role: 'system',
              text: data.generation_type === 'image'
                ? `[Generating image: ${data.prompt || ''}...]`
                : `[Generating document: ${data.title || ''}...]`,
              timestamp: new Date(),
              isStreaming: true,
            }
          ]);
          break;

        case 'generation_error':
          // BUG-026: Show generation error
          setMessages(prev => [
            ...prev,
            {
              id: `${Date.now()}-gen-error`,
              role: 'system',
              text: `[Generation failed: ${data.error || 'Unknown error'}]`,
              timestamp: new Date(),
            }
          ]);
          break;

        case 'generated_image':
          // BUG-016: Handle generated image from voice request
          if (data.image_base64) {
            // Remove the "generating" placeholder
            setMessages(prev => [
              ...prev.filter(m => !(m.isStreaming && m.text.includes('[Generating image'))),
              {
                id: `${Date.now()}-image`,
                role: 'system',
                text: `[Generated Image: ${data.prompt || 'Image'}]`,
                timestamp: new Date(),
                imageBase64: data.image_base64,
                imageMimeType: data.mime_type || 'image/jpeg',
                imageId: data.image_id,
              }
            ]);
            // BUG-026: Notify DownloadsTab to refresh
            window.dispatchEvent(new CustomEvent('dryvox:downloads-refresh'));
          }
          break;

        case 'generated_document':
          // EPIC-17: Handle generated document from voice request
          if (data.document_id) {
            // Remove the "generating" placeholder
            setMessages(prev => [
              ...prev.filter(m => !(m.isStreaming && m.text.includes('[Generating document'))),
              {
                id: `doc-${data.document_id}`,
                role: 'system',
                text: `[Generated Document: ${data.title || 'Document'}]`,
                timestamp: new Date(),
                documentId: data.document_id,
                documentTitle: data.title,
                documentType: data.document_type,
                documentFilename: data.filename,
                documentMimeType: data.mime_type,
              }
            ]);
            // BUG-026: Notify DownloadsTab to refresh
            window.dispatchEvent(new CustomEvent('dryvox:downloads-refresh'));
          }
          break;
      }
    } catch (e) {
      console.error('Error handling WebSocket message:', e);
    }
  }, [playBinaryAudio]);

  // Handle connection errors
  const handleConnectionError = useCallback((errorMsg: string) => {
    if (isManualDisconnectRef.current) return;
    
    console.error("Gemini Live Connection Error:", errorMsg);
    
    if (reconnectAttemptRef.current < MAX_RETRIES) {
      const attempt = reconnectAttemptRef.current + 1;
      const delay = Math.min(1000 * Math.pow(1.5, attempt - 1), 5000);
      setStatus('reconnecting');
      setError(`Connection lost. Retrying... (${attempt}/${MAX_RETRIES})`);
      reconnectAttemptRef.current = attempt;
      
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    } else {
      setError("Connection failed. Please try again.");
      setStatus('error');
      reconnectAttemptRef.current = 0;
    }
  }, []);

  // Start video capture
  const startVideo = useCallback(async (mode: 'user' | 'environment' = facingMode) => {
    try {
      // Stop existing stream
      if (videoStreamRef.current) {
        videoStreamRef.current.getTracks().forEach(track => track.stop());
      }

      // Request camera with fallback
      let stream: MediaStream;
      try {
        // Use { exact } so mobile browsers actually switch cameras (BUG-023 fix)
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: { exact: mode },
            frameRate: { ideal: 15, max: 30 }
          }
        });
      } catch (e) {
        // Fallback: try as ideal hint (for devices with only one camera)
        console.warn("Camera with exact facingMode failed, trying as ideal:", e);
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: mode,
            frameRate: { ideal: 15, max: 30 }
          }
        });
      }

      videoStreamRef.current = stream;

      // Set up processing video element (for frame capture to Gemini)
      if (processingVideoRef.current) {
        processingVideoRef.current.srcObject = stream;
        await processingVideoRef.current.play().catch(console.error);
      }

      // Set isVideoActive first so the preview DOM element renders
      setIsVideoActive(true);

      // BUG-030: Assign preview stream with retry. On first start, previewVideoRef
      // may be null because React hasn't rendered the video element yet. Retry up
      // to 10 frames to ensure we don't silently fail and leave a black preview.
      const assignPreview = (retriesLeft: number) => {
        if (previewVideoRef.current) {
          previewVideoRef.current.srcObject = stream;
          previewVideoRef.current.play().catch(console.error);
          console.log("Preview video assigned to stream");
        } else if (retriesLeft > 0) {
          requestAnimationFrame(() => assignPreview(retriesLeft - 1));
        } else {
          console.warn("BUG-030: previewVideoRef never became available after 10 frames");
        }
      };
      assignPreview(10);
      console.log("Video started successfully with mode:", mode);
    } catch (err) {
      console.error("Failed to start video:", err);
      setError("Could not access camera. Please check permissions.");
      setIsVideoActive(false);
    }
  }, [facingMode]);

  // Set up preview video when it becomes available or stream changes
  // Watches both isVideoActive (first activation) and facingMode (camera switch)
  useEffect(() => {
    if (!isVideoActive || !videoStreamRef.current) return;

    const setupPreview = () => {
      if (previewVideoRef.current && videoStreamRef.current) {
        const stream = videoStreamRef.current;
        const preview = previewVideoRef.current;

        if (preview.srcObject !== stream) {
          preview.srcObject = stream;
          preview.muted = true;
          preview.playsInline = true;
          preview.play().catch(console.error);
          console.log("Preview video connected to stream");
        }
      }
    };

    // Try immediately, then retry after frame paint if ref not ready
    if (previewVideoRef.current) {
      setupPreview();
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(setupPreview);
      });
    }
  }, [isVideoActive, facingMode]);

  // Stop video capture
  const stopVideo = useCallback(() => {
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach(track => track.stop());
      videoStreamRef.current = null;
    }
    if (processingVideoRef.current) processingVideoRef.current.srcObject = null;
    if (previewVideoRef.current) previewVideoRef.current.srcObject = null;
    setIsVideoActive(false);
  }, []);

  // Switch camera
  const switchCamera = useCallback(async () => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    if (isVideoActive) await startVideo(newMode);
  }, [facingMode, isVideoActive, startVideo]);

  // Send video frames when connected
  useEffect(() => {
    if (status === 'connected' && isVideoActive && wsRef.current) {
      if (videoIntervalRef.current) clearInterval(videoIntervalRef.current);
      
      videoIntervalRef.current = window.setInterval(() => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        
        const video = processingVideoRef.current;
        const canvas = canvasRef.current;
        
        if (video && canvas && video.readyState >= 2) {
          const ctx = canvas.getContext('2d');
          if (ctx && video.videoWidth > 0) {
            const scale = Math.min(1, 640 / video.videoWidth);
            canvas.width = video.videoWidth * scale;
            canvas.height = video.videoHeight * scale;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            const base64Data = canvas.toDataURL('image/jpeg', 0.6).split(',')[1];
            try {
              wsRef.current.send(JSON.stringify({
                type: 'image',
                data: base64Data,
                mimeType: 'image/jpeg'
              }));
            } catch (e) {
              console.error("Error sending video frame:", e);
            }
          }
        }
      }, 500); // Send frame every 500ms
    } else {
      if (videoIntervalRef.current) {
        clearInterval(videoIntervalRef.current);
        videoIntervalRef.current = null;
      }
    }
    
    return () => {
      if (videoIntervalRef.current) clearInterval(videoIntervalRef.current);
    };
  }, [status, isVideoActive]);

  // Send image file (also sends to Gemini for immediate visual analysis)
  const sendImage = useCallback(async (file: File) => {
    if (status !== 'connected' || !wsRef.current) {
      setError("Connect to DryVox first to share images.");
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const base64String = arrayBufferToBase64(arrayBuffer);

      wsRef.current.send(JSON.stringify({
        type: 'image',
        data: base64String,
        mimeType: file.type || 'image/jpeg'
      }));

      setMessages(prev => [
        ...prev,
        {
          id: `${Date.now()}-sys`,
          role: 'system',
          text: `[Sent Image: ${file.name}]`,
          timestamp: new Date()
        }
      ]);
    } catch (e) {
      console.error("Failed to send image:", e);
      setError("Failed to send image.");
    }
  }, [status]);

  // BUG-032: Capture a high-quality photo from the active camera stream.
  // Returns a Blob (JPEG) that can be uploaded to backend for OCR/context storage.
  const capturePhoto = useCallback((): Blob | null => {
    const video = processingVideoRef.current;
    if (!video || !isVideoActive || video.readyState < 2) {
      console.warn("Cannot capture photo: camera not active");
      return null;
    }

    const canvas = document.createElement('canvas');
    // Use full camera resolution (not the downscaled 640px used for streaming)
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob synchronously via dataURL (toBlob is async and harder to use here)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9); // High quality
    const byteString = atob(dataUrl.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: 'image/jpeg' });
  }, [isVideoActive]);

  // Set up audio input (microphone) - can be called separately from connect
  const setupAudioInput = useCallback(async () => {
    if (audioStreamRef.current) return; // Already set up

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 16000
        }
      });
      audioStreamRef.current = audioStream;

      // Create input audio context if not exists
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!inputAudioContextRef.current) {
        const inputCtx = new AudioContextClass({ sampleRate: 16000, latencyHint: 'interactive' });
        await inputCtx.resume();
        inputAudioContextRef.current = inputCtx;
        const inputNode = inputCtx.createGain();
        inputNodeRef.current = inputNode;
      }

      const inputCtx = inputAudioContextRef.current;
      const inputNode = inputNodeRef.current!;

      // Set up audio input processing
      const source = inputCtx.createMediaStreamSource(audioStream);
      const processor = inputCtx.createScriptProcessor(2048, 1, 1);
      scriptProcessorRef.current = processor;

      processor.onaudioprocess = (e) => {
        if (inputNode.gain.value === 0) return;
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        const inputData = e.inputBuffer.getChannelData(0);
        const vol = calculateVolume(inputData);
        setVolume(vol);

        // Reset idle timer on significant audio activity (BUG-011 fix)
        if (vol > 0.02) {
          resetIdleTimer();
        }

        // Send binary audio directly - no base64/JSON overhead
        const pcmBytes = createPcmBytes(inputData);
        try {
          wsRef.current.send(pcmBytes);
        } catch (e) {
          // Ignore send errors during normal operation
        }
      };

      source.connect(processor);
      processor.connect(inputCtx.destination);
      setIsVoiceEnabled(true);
      console.log('Voice input enabled');
      return true;
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError("Microphone access denied. You can still use text.");
      } else {
        setError("Could not access microphone. You can still use text.");
      }
      return false;
    }
  }, []);

  // Enable voice (microphone) after connection
  const enableVoice = useCallback(async () => {
    if (status !== 'connected') {
      // Connect with voice if not connected
      await connect({ enableVoice: true });
      return;
    }
    await setupAudioInput();
  }, [status, setupAudioInput]);

  // Disable voice (stop microphone)
  const disableVoice = useCallback(() => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }
    setIsVoiceEnabled(false);
    setVolume(0);
    console.log('Voice input disabled');
  }, []);

  // Connect to backend WebSocket proxy
  const connect = useCallback(async (options?: { enableVoice?: boolean }) => {
    const shouldEnableVoice = options?.enableVoice ?? true; // Default to enabling voice

    isManualDisconnectRef.current = false;
    reconnectAttemptRef.current = 0;
    setMessages([]);
    setStatus('connecting');
    setError(null);

    try {
      // Get auth token
      const token = localStorage.getItem('dryvox_access_token');
      if (!token) {
        setError("Please log in to use Live mode.");
        setStatus('error');
        return;
      }

      // Get selected document context from store
      const documentContext = useDocumentContextStore.getState().getSelectedContextText();
      if (documentContext) {
        console.log(`[useGeminiLive] Connecting with ${documentContext.length} chars of document context from ${useDocumentContextStore.getState().selectedDocumentIds.length} selected files`);
      }

      // Create output audio context for playback (always needed to hear responses)
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!outputAudioContextRef.current) {
        const outputCtx = new AudioContextClass({ sampleRate: 24000, latencyHint: 'interactive' });
        await outputCtx.resume();
        outputAudioContextRef.current = outputCtx;
        const outputNode = outputCtx.createGain();
        outputNodeRef.current = outputNode;
        outputNode.connect(outputCtx.destination);
      }

      // Connect to backend WebSocket proxy
      // Document context is sent as a message after connection (to avoid URL length limits)
      const wsUrl = getWsUrl(userLevel, token);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        console.log('Connected to backend voice-live proxy');

        // Send document context as first message if available
        // This avoids URL length limits for large documents
        if (documentContext && documentContext.trim()) {
          console.log(`[useGeminiLive] Sending ${documentContext.length} chars of document context via WebSocket message`);
          ws.send(JSON.stringify({
            type: 'set_context',
            document_context: documentContext,
          }));
        }

        // Enable voice input if requested
        if (shouldEnableVoice) {
          await setupAudioInput();
        }
        // Start idle timer (BUG-011 fix)
        resetIdleTimer();
      };

      ws.onmessage = handleWebSocketMessage;

      ws.onclose = (e) => {
        if (!isManualDisconnectRef.current) {
          handleConnectionError(`Connection closed (Code: ${e.code})`);
        } else {
          setStatus('disconnected');
          setIsVoiceEnabled(false);
        }
      };

      ws.onerror = () => {
        handleConnectionError('WebSocket error');
      };

    } catch (err: any) {
      handleConnectionError(err.message || 'Connection failed');
    }
  }, [userLevel, handleWebSocketMessage, handleConnectionError, setupAudioInput]);

  // Send text message - auto-connects if needed (text-only mode)
  // BUG-015 fix: Wait for full Gemini connection using ref to avoid closure issues
  const sendText = useCallback(async (text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText) return;

    // Auto-connect for text-only if not connected
    if (!isFullyConnectedRef.current) {
      // Reset the ref before connecting
      isFullyConnectedRef.current = false;
      // Connect without voice (text-only mode)
      await connect({ enableVoice: false });
    }

    // Wait for full connection (backend + Gemini ready)
    // isFullyConnectedRef is set to true when we receive 'connected' message
    const maxWait = 10000; // 10 seconds max for full Gemini connection
    const startTime = Date.now();
    while (Date.now() - startTime < maxWait) {
      if (isFullyConnectedRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Final check - must be fully connected
    if (!isFullyConnectedRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setError("Could not connect to voice service. Please try again.");
      return;
    }

    try {
      wsRef.current.send(JSON.stringify({
        type: 'text',
        content: trimmedText
      }));

      // Add user message to transcript
      setMessages(prev => [
        ...prev,
        {
          id: `${Date.now()}-user-text`,
          role: 'user',
          text: trimmedText,
          timestamp: new Date()
        }
      ]);

      // Reset idle timer on text activity (BUG-011)
      resetIdleTimer();
    } catch (e) {
      console.error("Failed to send text:", e);
      setError("Failed to send message.");
    }
  }, [connect, resetIdleTimer]);

  // Reconnect with updated document context (BUG-021 fix)
  // When documents are uploaded during an active session, we need to reconnect
  // so the document context is included in the system prompt (like MVP behavior)
  const sendDocumentContext = useCallback(async () => {
    const documentContext = useDocumentContextStore.getState().getSelectedContextText();
    if (!documentContext || !documentContext.trim()) {
      console.log('[useGeminiLive] No document context to send');
      return false;
    }

    console.log(`[useGeminiLive] Document uploaded - reconnecting to include ${documentContext.length} chars in system prompt`);

    // Add system message to transcript
    setMessages(prev => [
      ...prev,
      {
        id: `${Date.now()}-sys-context`,
        role: 'system',
        text: `[Document uploaded - reconnecting to load context...]`,
        timestamp: new Date()
      }
    ]);

    // Reconnect to include document in system prompt
    // The connect function will pick up the document context from the store
    if (isFullyConnectedRef.current && wsRef.current) {
      // Close current connection
      isManualDisconnectRef.current = true;
      try {
        wsRef.current.close();
      } catch (e) {
        console.error("Error closing WebSocket:", e);
      }
      wsRef.current = null;
      isFullyConnectedRef.current = false;

      // Small delay then reconnect
      await new Promise(resolve => setTimeout(resolve, 500));
      isManualDisconnectRef.current = false;
      await connect({ enableVoice: true });

      setMessages(prev => [
        ...prev,
        {
          id: `${Date.now()}-sys-ready`,
          role: 'system',
          text: `[Document context loaded - ready to answer questions]`,
          timestamp: new Date()
        }
      ]);
    }

    return true;
  }, [connect]);

  // Disconnect
  const disconnect = useCallback(() => {
    isManualDisconnectRef.current = true;

    // Clear idle timers (BUG-011 fix)
    clearIdleTimers();

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptRef.current = 0;
    
    // Send disconnect message
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ type: 'disconnect' }));
        wsRef.current.close();
      } catch (e) {
        console.error("Error closing WebSocket:", e);
      }
    }
    wsRef.current = null;
    
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    
    stopVideo();
    
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }
    
    if (inputAudioContextRef.current) {
      inputAudioContextRef.current.close();
      inputAudioContextRef.current = null;
    }
    
    if (outputAudioContextRef.current) {
      outputAudioContextRef.current.close();
      outputAudioContextRef.current = null;
    }
    
    sourcesRef.current.forEach(source => {
      try { source.stop(); } catch (e) {}
    });
    sourcesRef.current.clear();

    // Clean up debounce timer
    if (interruptDebounceRef.current) {
      clearTimeout(interruptDebounceRef.current);
      interruptDebounceRef.current = null;
    }

    setStatus('disconnected');
    setVolume(0);
    setIsVoiceEnabled(false);
    nextStartTimeRef.current = 0;
    isFullyConnectedRef.current = false;
  }, [stopVideo]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (inputNodeRef.current) {
      const newMuteState = !isMuted;
      setIsMuted(newMuteState);
      inputNodeRef.current.gain.value = newMuteState ? 0 : 1;
    }
  }, [isMuted]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    status,
    error,
    volume,
    isMuted,
    isVoiceEnabled,
    isVideoActive,
    facingMode,
    hasMultipleCameras,
    messages,
    pendingReviews,
    acknowledgeMessage,
    submitFeedback,
    canDisconnect,
    connect,
    disconnect,
    toggleMute,
    enableVoice,
    disableVoice,
    startVideo,
    stopVideo,
    switchCamera,
    sendImage,
    capturePhoto, // BUG-032: Capture high-res photo from camera for context
    sendText,
    sendDocumentContext, // BUG-021: Send document context to active session
    previewVideoRef,
    // Idle timeout (BUG-011 fix)
    idleWarning,
    resetIdleTimer,
  };
}

export default useGeminiLive;
